import { useState, useEffect, useCallback } from "react";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, UserCog, Pencil, Trash2, X, Shield } from "lucide-react";

const AUTH_HEADERS = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const ROLE_LABELS = {
  master: { label: "Master", cls: "bg-amber-100 text-amber-700", desc: "Control total del club" },
  operador: { label: "Operador", cls: "bg-blue-100 text-blue-700", desc: "Crea rankings, etapas, tee times" },
  capturista: { label: "Capturista", cls: "bg-gray-100 text-gray-600", desc: "Solo carga scores y resultados" },
};

export default function ClubTeam() {
  const ctx = useClub();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", club_role: "capturista" });

  const fetchTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/team`, {
        credentials: "include",
        headers: AUTH_HEADERS(),
      });
      if (res.ok) setMembers(await res.json());
    } catch {
      toast.error("Error al cargar equipo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", password: "", club_role: "capturista" });
    setEditUser(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (!editUser && !form.email.trim()) return toast.error("Email requerido");
    if (!editUser && !form.password) return toast.error("Contrasena requerida");

    try {
      const url = editUser ? `${API}/club/team/${editUser.user_id}` : `${API}/club/team`;
      const method = editUser ? "PUT" : "POST";
      const payload = { name: form.name.trim(), phone: form.phone.trim(), club_role: form.club_role };
      if (!editUser) {
        payload.email = form.email.trim();
        payload.password = form.password;
      }
      if (editUser && form.password) payload.password = form.password;

      const res = await fetch(url, { method, credentials: "include", headers: AUTH_HEADERS(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success(editUser ? "Usuario actualizado" : "Usuario creado");
      resetForm();
      fetchTeam();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API}/club/team/${deleteTarget}`, {
        method: "DELETE", credentials: "include", headers: AUTH_HEADERS(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success("Usuario eliminado");
      setDeleteTarget(null);
      fetchTeam();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al eliminar");
    }
  };

  const handleToggleStatus = async (member) => {
    const newStatus = member.status === "active" ? "inactive" : "active";
    try {
      const res = await fetch(`${API}/club/team/${member.user_id}`, {
        method: "PUT", credentials: "include", headers: AUTH_HEADERS(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(newStatus === "active" ? "Usuario activado" : "Usuario desactivado");
        fetchTeam();
      }
    } catch {
      toast.error("Error al cambiar status");
    }
  };

  const openEdit = (m) => {
    setEditUser(m);
    setForm({ name: m.name, email: m.email, phone: m.phone || "", password: "", club_role: m.club_role || "capturista" });
    setShowForm(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="club-team-title">
            <UserCog className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Usuarios del Club
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{ctx?.club?.name}</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
          data-testid="add-team-member-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      {/* Role legend */}
      <div className="flex flex-wrap gap-4 mb-6">
        {Object.entries(ROLE_LABELS).map(([key, val]) => (
          <div key={key} className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full font-medium ${val.cls}`}>{val.label}</span>
            <span className="text-[#94A3B8]">{val.desc}</span>
          </div>
        ))}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="team-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-[#0F172A]">{editUser ? "Editar Usuario" : "Nuevo Usuario"}</h3>
              <button onClick={resetForm}><X className="w-5 h-5 text-[#64748B]" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre completo" data-testid="team-form-name" />
              </div>
              {!editUser && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@club.com" data-testid="team-form-email" />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Opcional" data-testid="team-form-phone" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Rol *</Label>
                <Select value={form.club_role} onValueChange={(v) => setForm({ ...form, club_role: v })}>
                  <SelectTrigger data-testid="team-form-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operador">Operador — Crea rankings, etapas, tee times</SelectItem>
                    <SelectItem value="capturista">Capturista — Solo carga scores</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{editUser ? "Nueva Contrasena (dejar vacio para no cambiar)" : "Contrasena *"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editUser ? "Sin cambios" : "Min. 6 caracteres"} data-testid="team-form-password" />
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={resetForm}>Cancelar</Button>
              <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} data-testid="team-form-save-btn">
                {editUser ? "Guardar Cambios" : "Crear Usuario"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Member list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : members.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-12 text-center">
            <UserCog className="w-10 h-10 text-[#64748B] mx-auto mb-3 opacity-50" />
            <p className="text-[#64748B]">No hay usuarios del club</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="team-members-list">
          {members.map((m) => {
            const roleInfo = ROLE_LABELS[m.club_role] || ROLE_LABELS.capturista;
            const isMaster = m.club_role === "master";
            return (
              <Card key={m.user_id} className="card-default" data-testid={`team-member-${m.user_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white font-bold text-sm">
                      {m.name?.[0] || "?"}
                    </div>
                    <div>
                      <p className="font-medium text-[#0F172A]">{m.name}</p>
                      <p className="text-xs text-[#64748B]">{m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleInfo.cls}`}>{roleInfo.label}</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${m.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {m.status === "active" ? "Activo" : "Inactivo"}
                    </span>
                    {!isMaster && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(m)} data-testid={`team-toggle-${m.user_id}`}>
                          {m.status === "active" ? "Desactivar" : "Activar"}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`team-edit-${m.user_id}`}>
                          <Pencil className="w-4 h-4 text-[#64748B]" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(m.user_id)} data-testid={`team-delete-${m.user_id}`}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminara la cuenta y el acceso de este usuario al club.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-team-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
